import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  ArrowLeft,
  Search,
  BarChart3,
  Megaphone,
  ShoppingCart,
  Shield,
  CheckSquare,
  AlertTriangle,
  MousePointer,
  Link2,
  Settings,
  Eye,
} from 'lucide-react';

const IMG = '/img/help/google-integrations';

export default function GoogleIntegrationsGuide() {
  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="mb-6">
        <Link
          to="/help/guides"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to User Guides
        </Link>
        <h1 className="text-3xl font-bold mb-2">Connecting Google Services</h1>
        <p className="text-muted-foreground">
          How to connect Google Search Console, Analytics 4, Ads, and Merchant Center to PageMind
        </p>
        <div className="flex gap-2 mt-3">
          <Badge>Content Manager</Badge>
          <Badge>Super Admin</Badge>
        </div>
      </div>

      {/* Overview */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Overview
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm">
            PageMind connects to your Google account via OAuth to pull performance data from up to four services:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
            <div className="flex items-start gap-2 text-sm">
              <Search className="h-4 w-4 mt-0.5 text-blue-500 shrink-0" />
              <div>
                <strong>Search Console</strong> &mdash; search visibility, queries, and positions
              </div>
            </div>
            <div className="flex items-start gap-2 text-sm">
              <BarChart3 className="h-4 w-4 mt-0.5 text-green-500 shrink-0" />
              <div>
                <strong>Analytics 4</strong> &mdash; sessions, engagement, and conversions
              </div>
            </div>
            <div className="flex items-start gap-2 text-sm">
              <Megaphone className="h-4 w-4 mt-0.5 text-orange-500 shrink-0" />
              <div>
                <strong>Google Ads</strong> &mdash; search terms, Quality Score, and landing pages
              </div>
            </div>
            <div className="flex items-start gap-2 text-sm">
              <ShoppingCart className="h-4 w-4 mt-0.5 text-purple-500 shrink-0" />
              <div>
                <strong>Merchant Center</strong> &mdash; product status, disapprovals, and Shopping performance
              </div>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mt-3">
            You only need to authenticate once &mdash; a single Google sign-in grants access to all the services you select.
          </p>
        </CardContent>
      </Card>

      {/* Prerequisites */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Prerequisites
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm">
            <li className="flex items-start gap-2">
              <span className="text-primary">-</span>
              <span><strong>Content Manager</strong> or <strong>Super Admin</strong> access on the site you want to connect</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">-</span>
              <span>A <strong>Google account</strong> with access to the Search Console, Analytics, Ads, or Merchant Center properties you want to link</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">-</span>
              <span>Make sure <strong>the correct site is selected</strong> in the site selector at the top of the page before starting</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Step 1: Navigate */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MousePointer className="h-5 w-5 text-blue-500" />
            Step 1: Open Google Integrations
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-sm text-muted-foreground">
            There are two ways to reach the Google Integrations page:
          </p>

          <div>
            <h4 className="font-medium mb-2">Option A: Use the Search Menu</h4>
            <p className="text-sm mb-3">
              Press <kbd className="px-1.5 py-0.5 border rounded text-xs font-mono">Cmd + K</kbd> (Mac)
              or <kbd className="px-1.5 py-0.5 border rounded text-xs font-mono">Ctrl + K</kbd> (Windows)
              to open the search menu, then type <strong>"Google Integrations"</strong>.
            </p>
            <div className="space-y-3">
              <img
                src={`${IMG}/02-search-menu.png`}
                alt="Search menu opened with Cmd+K"
                className="rounded-lg border w-full max-w-lg"
              />
              <img
                src={`${IMG}/03-search-google-integrations.png`}
                alt="Typing 'google integratio' in the search menu shows the Google Integrations result"
                className="rounded-lg border w-full max-w-lg"
              />
            </div>
          </div>

          <Separator />

          <div>
            <h4 className="font-medium mb-2">Option B: Use the Sidebar</h4>
            <p className="text-sm">
              In the left sidebar, scroll down to the <strong>Sites &amp; Structure</strong> section and click <strong>Google Integrations</strong>.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Step 2: Select Services */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-green-500" />
            Step 2: Select Services to Connect
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-sm text-muted-foreground">
            The <strong>Connection Status</strong> tab shows four service cards. Click on each service you want to connect &mdash; selected services are highlighted in blue.
          </p>

          <div>
            <h4 className="font-medium mb-2">Select one or more services</h4>
            <p className="text-sm mb-3">
              Click on the service cards to select them. The counter at the bottom shows how many are selected.
            </p>
            <img
              src={`${IMG}/04-connection-status-select-services.png`}
              alt="Google Integrations page with Search Console selected"
              className="rounded-lg border w-full"
            />
            <p className="text-sm text-muted-foreground mt-2">
              Above: only Search Console is selected (1 service).
            </p>
          </div>

          <Separator />

          <div>
            <h4 className="font-medium mb-2">We recommend selecting all four services</h4>
            <p className="text-sm mb-3">
              This gives PageMind the fullest picture of your site's performance. You can always change this later.
            </p>
            <img
              src={`${IMG}/05-all-services-selected.png`}
              alt="All four services selected - Search Console, Analytics 4, Google Ads, and Merchant Center"
              className="rounded-lg border w-full"
            />
            <p className="text-sm text-muted-foreground mt-2">
              Above: all four services selected (4 services).
            </p>
          </div>

          <Separator />

          <div>
            <p className="text-sm">
              Once you've selected the services you want, click the <strong>"Connect with Google"</strong> button in the bottom-right corner. A new browser window will open for Google authentication.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Step 3: Google OAuth */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-orange-500" />
            Step 3: Authenticate with Google
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-sm text-muted-foreground">
            A popup window opens the standard Google sign-in flow. Follow these three screens:
          </p>

          <div>
            <h4 className="font-medium mb-2">3.1: Choose your Google account</h4>
            <p className="text-sm mb-3">
              Select the Google account that has access to your Search Console, Analytics, Ads, or Merchant Center properties.
              If your company uses Google Workspace, pick your work account.
            </p>
            <img
              src={`${IMG}/06-google-choose-account.png`}
              alt="Google account chooser showing available accounts"
              className="rounded-lg border w-full max-w-lg"
            />
          </div>

          <Separator />

          <div>
            <h4 className="font-medium mb-2">3.2: Confirm sign-in to PageMind.ai</h4>
            <p className="text-sm mb-3">
              Google shows what basic information PageMind will access (name, email).
              Click <strong>"Continue"</strong> to proceed.
            </p>
            <img
              src={`${IMG}/07-google-sign-in-continue.png`}
              alt="Google consent screen showing PageMind.ai will access name and email"
              className="rounded-lg border w-full max-w-lg"
            />
          </div>

          <Separator />

          <div>
            <h4 className="font-medium mb-2">3.3: Grant data access permissions</h4>
            <p className="text-sm mb-3">
              Google asks which specific permissions to grant. You will see a checkbox for each service.
              <strong> Make sure to check all the boxes</strong> (or click "Select all") so that PageMind can read data from all the services you selected.
            </p>
            <div className="space-y-3">
              <img
                src={`${IMG}/09-google-permissions-unchecked.png`}
                alt="Google permissions screen with checkboxes unchecked"
                className="rounded-lg border w-full max-w-lg"
              />
              <p className="text-sm text-muted-foreground">
                Above: permissions are unchecked by default. You must check them.
              </p>
              <img
                src={`${IMG}/10-google-permissions-all-checked.png`}
                alt="Google permissions screen with all checkboxes checked"
                className="rounded-lg border w-full max-w-lg"
              />
              <p className="text-sm text-muted-foreground">
                Above: all permissions checked. Click <strong>"Continue"</strong> to complete the connection.
              </p>
            </div>

            <div className="bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 mt-4">
              <p className="text-sm flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-yellow-600 shrink-0 mt-0.5" />
                <span>
                  <strong>Important:</strong> If you skip any checkbox, that service will show as "Not Connected" in PageMind.
                  You would need to disconnect and reconnect to fix it.
                </span>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Step 4: Verify Connection */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-cyan-500" />
            Step 4: Verify the Connection
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-sm text-muted-foreground">
            After completing the Google sign-in, close the popup and return to PageMind.
            The page refreshes automatically and shows the <strong>Connection Status</strong> tab with your connected services.
          </p>

          <img
            src={`${IMG}/11-connection-status-connected.png`}
            alt="Connection Status showing all four services connected with green badges"
            className="rounded-lg border w-full"
          />

          <div className="text-sm space-y-2">
            <p>You should see:</p>
            <ul className="space-y-1 ml-4 list-disc">
              <li>A green <strong>"Connected"</strong> badge next to each service</li>
              <li>Your <strong>email address</strong> and auth method (OAuth) in the Connected Account card</li>
              <li>For Search Console: the selected property URL</li>
              <li>For Analytics 4: the property name</li>
              <li>For Google Ads: the Customer ID</li>
              <li>For Merchant Center: the Merchant ID (if configured)</li>
            </ul>
            <p className="text-muted-foreground mt-2">
              Each service card also has a <strong>"Sync Now"</strong> button to trigger an immediate data sync.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Step 5: Configure Properties */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-indigo-500" />
            Step 5: Configure Properties
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-sm text-muted-foreground">
            Switch to the <strong>Configure</strong> tab to select which specific property or account PageMind should sync data from.
          </p>

          <img
            src={`${IMG}/12-configure-tab-selectors.png`}
            alt="Configure tab showing dropdown selectors for Search Console, Analytics 4, and Google Ads"
            className="rounded-lg border w-full"
          />

          <div className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-3">
              <p className="font-medium text-sm mb-1 flex items-center gap-2">
                <Search className="h-4 w-4" /> Search Console Property
              </p>
              <p className="text-sm text-muted-foreground">
                Select the property that matches your site's domain (e.g., <code>https://www.example.com/</code>).
                If you have both a domain property and a URL-prefix property, the domain property provides more complete data.
              </p>
            </div>

            <div className="bg-muted/50 rounded-lg p-3">
              <p className="font-medium text-sm mb-1 flex items-center gap-2">
                <BarChart3 className="h-4 w-4" /> Analytics 4 Property
              </p>
              <p className="text-sm text-muted-foreground">
                Choose the GA4 property for your website. The dropdown shows the property name and account name to help you identify the right one.
              </p>
            </div>

            <div className="bg-muted/50 rounded-lg p-3">
              <p className="font-medium text-sm mb-1 flex items-center gap-2">
                <Megaphone className="h-4 w-4" /> Google Ads Account
              </p>
              <p className="text-sm text-muted-foreground">
                Select the Ads account that runs campaigns for this site. The dropdown shows the account name and Customer ID.
                Manager accounts are labeled with "[Manager]".
              </p>
            </div>
          </div>

          <p className="text-sm">
            Changes are saved automatically when you select a value from any dropdown.
          </p>
        </CardContent>
      </Card>

      {/* Troubleshooting */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
            Troubleshooting
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-medium text-sm">A service shows "Not Connected" after sign-in</h4>
            <p className="text-sm text-muted-foreground">
              You likely skipped its checkbox on the Google permissions screen.
              Click <strong>Disconnect</strong>, then reconnect and make sure to check all permission boxes.
            </p>
          </div>
          <Separator />
          <div>
            <h4 className="font-medium text-sm">The Configure dropdowns are empty</h4>
            <p className="text-sm text-muted-foreground">
              This means the Google account you signed in with doesn't have access to any properties for that service.
              Verify that you used the correct Google account and that you have at least "Viewer" access in Search Console, Analytics, or Ads.
            </p>
          </div>
          <Separator />
          <div>
            <h4 className="font-medium text-sm">The popup window didn't appear</h4>
            <p className="text-sm text-muted-foreground">
              Your browser may be blocking popups. Allow popups for <strong>admin.pagemind.ai</strong> and try again.
            </p>
          </div>
          <Separator />
          <div>
            <h4 className="font-medium text-sm">"Sync Now" fails with an error</h4>
            <p className="text-sm text-muted-foreground">
              Make sure you've selected a property/account in the Configure tab first. If the error persists, the OAuth token may have expired &mdash; disconnect and reconnect.
            </p>
          </div>
          <Separator />
          <div>
            <h4 className="font-medium text-sm">I don't see the Google Ads account dropdown</h4>
            <p className="text-sm text-muted-foreground">
              The Ads selector only appears in the Configure tab when Google Ads is connected.
              Make sure you selected "Google Ads" before clicking "Connect with Google", and that you checked the Ads permission checkbox.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Verification Checklist */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckSquare className="h-5 w-5 text-green-500" />
            Verification Checklist
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[
              'Correct site selected in the site selector',
              'All desired services selected before clicking "Connect with Google"',
              'All permission checkboxes checked on the Google consent screen',
              'Connection Status tab shows green "Connected" badges',
              'Search Console property selected in Configure tab',
              'Analytics 4 property selected in Configure tab',
              'Google Ads account selected in Configure tab',
              'First sync completed successfully for each service',
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="h-5 w-5 rounded border border-muted-foreground/30 shrink-0" />
                <span className="text-sm">{item}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Footer */}
      <Card className="bg-muted/50">
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">
            Need more help?{' '}
            <Link to="/help/faq" className="text-primary hover:underline">
              Check our FAQ
            </Link>{' '}
            or{' '}
            <Link to="/my-suggestions" className="text-primary hover:underline">
              submit a question
            </Link>.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
